import { batchActions } from 'redux-batched-actions';
import memoizeOne from 'memoize-one';
import { connect } from '@atlassian/jira-react-redux/src/index.tsx';
import type { BulkOpsShowFailedFlagActionPayload } from '../../../state/actions/bulk-ops-flags/error/index.tsx';
import { cardMultiSelect } from '../../../state/actions/cards/selection/index.tsx';
import { makeCardIdFromCardKey } from '../../../state/selectors/cards/index.tsx';
import type { Dispatch, State } from '../../../state/types.tsx';
import BulkActionsFailedFlag, { type BulkActionsFailedFlagProps } from './view.tsx';

export type ConnectedBulkActionsFailedFlagProps = BulkOpsShowFailedFlagActionPayload & {
	id: string | number;
	onDismissed: undefined | ((arg1: string | number) => void);
};

type GetCardIdFromCardKey = (key: string) => number;
type DispatchProps = {
	onRetry: () => void;
	onSelectFailedItems: (getCardIdFromCardKey: GetCardIdFromCardKey) => void;
};

type StateProps = { getCardIdFromCardKey: GetCardIdFromCardKey };

const mapDispatchToProps = (
	dispatch: Dispatch,
	{
		failedActionBuilders,
		onDismissed,
		id: flagId,
		failedIssueKeys,
		failedBatchIds,
	}: ConnectedBulkActionsFailedFlagProps,
): DispatchProps => ({
	onRetry: () => {
		onDismissed?.(flagId);

		if (failedActionBuilders.length < 1) {
			return;
		}

		const action = batchActions(
			failedActionBuilders.map((f) => {
				const builtAction = f();
				return {
					...builtAction,
					meta: {
						...builtAction.meta,
						failedBatchIds,
					},
				};
			}),
		);

		dispatch(action);
	},
	onSelectFailedItems: (getCardIdFromCardKey) => {
		onDismissed?.(flagId);
		const failedCardIds = failedIssueKeys.map((key) => getCardIdFromCardKey(key));
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		dispatch(cardMultiSelect(failedCardIds!));
	},
});

const onSelectFailedItemsFactory = memoizeOne(
	(
		getCardIdFromCardKey: GetCardIdFromCardKey,
		onSelectFailedItems: DispatchProps['onSelectFailedItems'],
	) =>
		() =>
			onSelectFailedItems(getCardIdFromCardKey),
);

const onViewSummaryFactory = memoizeOne(
	(
		failedIssueKeys: ConnectedBulkActionsFailedFlagProps['failedIssueKeys'],
		onDismissed: ConnectedBulkActionsFailedFlagProps['onDismissed'],
		flagId: ConnectedBulkActionsFailedFlagProps['id'],
	) =>
		() => {
			const cardKeys = failedIssueKeys.join(',');
			const jqlSearch = encodeURIComponent(`issueKey in (${cardKeys})`);
			const url = `/issues/?jql=${jqlSearch}`;

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.open(url, '_blank');
			onDismissed?.(flagId);
		},
);

export const mergeProps = (
	{ getCardIdFromCardKey }: StateProps,
	{ onRetry, onSelectFailedItems }: DispatchProps,
	{
		failedActionBuilders: failedActions,
		failedIssueKeys,
		id: flagId,
		onDismissed,
		failedBatchIds,
		failureReason,
		...restOwnProps
	}: ConnectedBulkActionsFailedFlagProps,
): BulkActionsFailedFlagProps => {
	const hasReachedRetryLimit = failedBatchIds && failedBatchIds?.length > 2;
	const shouldShowRetryButton =
		failedActions.length > 0 && !hasReachedRetryLimit && failureReason !== 'validation';
	const shouldShowViewSummaryButton =
		failureReason !== 'network' || (failureReason === 'network' && !hasReachedRetryLimit);
	const shouldShowSelectFailedItemsButton = failureReason === 'validation';
	const shouldShowHelpButton =
		hasReachedRetryLimit && failureReason !== 'network' && failureReason !== 'validation';

	return {
		failedCount: failedIssueKeys.length,
		onRetry,
		onSelectFailedItems: onSelectFailedItemsFactory(getCardIdFromCardKey, onSelectFailedItems),
		onViewSummary: onViewSummaryFactory(failedIssueKeys, onDismissed, flagId),
		showRetryButton: shouldShowRetryButton,
		showViewSummaryButton: shouldShowViewSummaryButton,
		showSelectFailedItemsButton: shouldShowSelectFailedItemsButton,
		showLearnMoreButton: !!shouldShowHelpButton,
		id: flagId,
		failureReason,
		onDismissed,
		...restOwnProps,
	};
};

const mapStateToProps = (state: State): StateProps => ({
	getCardIdFromCardKey: makeCardIdFromCardKey(state),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(BulkActionsFailedFlag);
